import { AlertTriangle, Check, ChevronRight, FlaskConical, FolderOpen, GitBranch, Loader2, Save, Sparkles } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useRef, useState } from "react"
import { ScrollArea } from "../components/ui"
import { selectDirectory } from "../electronAPIs/shell"
import { type IsGitDirectoryResponse, type ResolvePathResponse, initGit, isGitApiAvailable, isGitDirectory, resolvePath } from "../nestAPIs/git"
import { getPlatformAdapter } from "../platform/adapter"
import {
    PROJECT_COLOR_OPTIONS,
    automaticProjectColor,
    defaultProjectChars,
    normalizeProjectChars,
    normalizeProjectEmoji,
    parseProjectChars,
    projectColorAssignments,
    projectColorValue,
    projectIdentity,
    type ProjectColor,
} from "../projectAppearance"
import { useCodeNavigate } from "../routing"
import { useCodeStore } from "../store/context"
import type { Repo } from "../types"
import { generateProjectIdentities } from "../projectIdentityGeneration"
import { ProjectIcon } from "../components/sidebar/ProjectIcon"

interface WorkspaceSettingsPageProps {
    workspaceId: string
    repo: Repo
}

export const WorkspaceSettingsPage = observer(({ workspaceId, repo }: WorkspaceSettingsPageProps) => {
    const codeStore = useCodeStore()
    const navigate = useCodeNavigate()
    const canBrowseHostDirectories = getPlatformAdapter().capabilities.hostDirectoryPicker === "available"

    // Form state - initialize from repo
    const [name, setName] = useState(repo.name)
    const [inputValue, setInputValue] = useState(repo.path)
    const [colorSelection, setColorSelection] = useState<ProjectColor | "auto">(repo.color ?? "auto")
    const [projectChars, setProjectChars] = useState(projectIdentity(repo).projectChars)
    const [projectEmoji, setProjectEmoji] = useState(projectIdentity(repo).projectEmoji)
    const [generatingIdentity, setGeneratingIdentity] = useState(false)
    const [identityError, setIdentityError] = useState<string | null>(null)
    const previousRepo = useRef(repo)

    // Validation state
    const [isValidating, setIsValidating] = useState(false)
    const [pathInfo, setPathInfo] = useState<ResolvePathResponse | null>(null)
    const [gitInfo, setGitInfo] = useState<IsGitDirectoryResponse | null>(null)

    // Git init state
    const [isInitializingGit, setIsInitializingGit] = useState(false)
    const [initGitError, setInitGitError] = useState<string | null>(null)

    // Save state
    const [isSaving, setIsSaving] = useState(false)

    // Reset form state when repo changes (e.g., navigating to different workspace)
    useEffect(() => {
        const previous = previousRepo.current
        const changedProject = previous.id !== repo.id
        const before = projectIdentity(previous)
        const after = projectIdentity(repo)
        setName((current) => (changedProject || current === previous.name ? repo.name : current))
        setInputValue((current) => (changedProject || current === previous.path ? repo.path : current))
        setColorSelection((current) => (changedProject || current === (previous.color ?? "auto") ? (repo.color ?? "auto") : current))
        setProjectChars((current) => (changedProject || current === before.projectChars ? after.projectChars : current))
        setProjectEmoji((current) => (changedProject || current === before.projectEmoji ? after.projectEmoji : current))
        previousRepo.current = repo
    }, [repo])

    const handleGenerateIdentity = async () => {
        setGeneratingIdentity(true)
        setIdentityError(null)
        const requestedId = repo.id
        try {
            const generated = (await generateProjectIdentities([{ id: requestedId, name }], codeStore.defaultHarnessId)).get(requestedId)
            if (!generated) throw new Error("The AI did not return a valid emoji and two-letter identity. Try again.")
            if (previousRepo.current.id !== requestedId) return
            setProjectChars(generated.projectChars)
            setProjectEmoji(generated.projectEmoji)
        } catch (error) {
            setIdentityError(error instanceof Error ? error.message : String(error))
        } finally {
            setGeneratingIdentity(false)
        }
    }

    // Debounced path validation
    useEffect(() => {
        if (!inputValue.trim()) {
            setPathInfo(null)
            setGitInfo(null)
            return
        }

        const timer = setTimeout(async () => {
            if (!isGitApiAvailable()) return

            setIsValidating(true)
            setInitGitError(null)

            try {
                // Resolve the path first
                const resolved = await resolvePath({ path: inputValue.trim() })
                setPathInfo(resolved)

                // If it's a valid directory, check if it's a git repo
                if (resolved.exists && resolved.isDirectory) {
                    const git = await isGitDirectory({ directory: resolved.resolvedPath })
                    setGitInfo(git)
                } else {
                    setGitInfo(null)
                }
            } catch (error) {
                console.error("[WorkspaceSettingsPage] Error validating path:", error)
                setPathInfo(null)
                setGitInfo(null)
            } finally {
                setIsValidating(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [inputValue])

    const handleBrowse = async () => {
        const selected = await selectDirectory()
        if (selected) {
            setInputValue(selected)
        }
    }

    const handleInitGit = async () => {
        if (!pathInfo?.resolvedPath) return

        setIsInitializingGit(true)
        setInitGitError(null)

        try {
            const result = await initGit({ directory: pathInfo.resolvedPath })
            if (result.success) {
                // Re-check git status
                const git = await isGitDirectory({ directory: pathInfo.resolvedPath })
                setGitInfo(git)
            } else {
                setInitGitError(result.error || "Failed to initialize git repository")
            }
        } catch (error) {
            setInitGitError(error instanceof Error ? error.message : "Failed to initialize git repository")
        } finally {
            setIsInitializingGit(false)
        }
    }

    const handleSave = async () => {
        if (!canSave) return

        setIsSaving(true)
        try {
            const updates: Partial<Pick<Repo, "name" | "path" | "color" | "projectChars" | "projectEmoji">> = {}

            if (name.trim() !== repo.name) {
                updates.name = name.trim()
            }

            const resolvedPath = pathInfo?.resolvedPath || inputValue.trim()
            const isPathChanging = resolvedPath !== repo.path
            if (isPathChanging) {
                updates.path = resolvedPath
            }

            if (colorChanged) {
                updates.color = colorSelection === "auto" ? undefined : colorSelection
            }

            if (charsChanged) updates.projectChars = projectChars
            if (emojiChanged) updates.projectEmoji = projectEmoji || undefined

            if (Object.keys(updates).length > 0) {
                await codeStore.repos.updateRepo(workspaceId, updates)

                // Reload the page if path changed to refresh the repository environment
                if (isPathChanging) {
                    window.location.reload()
                }
            }
        } finally {
            setIsSaving(false)
        }
    }

    // Derived state
    const hasCodeModules = isGitApiAvailable()
    const hasValidDirectory = pathInfo?.exists && pathInfo?.isDirectory
    const isGitRepo = gitInfo && "isGitDirectory" in gitInfo && gitInfo.isGitDirectory
    const showGitWarning = hasValidDirectory && !isGitRepo && !isValidating

    // Check if there are changes
    const resolvedPath = pathInfo?.resolvedPath || inputValue.trim()
    const nameChanged = name.trim() !== repo.name
    const pathChanged = resolvedPath !== repo.path
    const colorChanged = colorSelection !== (repo.color ?? "auto")
    const savedIdentity = projectIdentity(repo)
    const charsChanged = projectChars !== savedIdentity.projectChars
    const emojiChanged = projectEmoji !== savedIdentity.projectEmoji
    const hasChanges = nameChanged || pathChanged || colorChanged || charsChanged || emojiChanged

    const automaticColors = projectColorAssignments(codeStore.repos.repos.filter((candidate) => !candidate.archived))
    const previewColor = colorSelection === "auto" ? (automaticColors.get(repo.id) ?? automaticProjectColor(repo.id)) : projectColorValue(colorSelection)
    const defaultChars = defaultProjectChars(name)
    const validIdentity = parseProjectChars(projectChars) === projectChars && (!projectEmoji || !!normalizeProjectEmoji(projectEmoji))

    // Can save if: has changes, name is not empty, path is valid (or unchanged)
    const pathIsValid = !pathChanged || (hasValidDirectory && !isValidating)
    const canSave = hasChanges && name.trim().length > 0 && pathIsValid && validIdentity && !isSaving && !generatingIdentity

    return (
        <ScrollArea className="h-full" viewportClassName="p-0">
            <div className="flex flex-col items-center pt-12 p-8">
                <div className="w-full max-w-2xl">
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-2">
                            <button
                                type="button"
                                onClick={() => navigate.go("CodeWorkspaceTaskCreate", { workspaceId })}
                                className="btn cursor-pointer border-none bg-transparent text-muted transition-colors hover:text-base-content"
                                aria-label="Back to project"
                            >
                                <ChevronRight size="1.5rem" className="rotate-180" />
                            </button>
                            <h1 className="text-2xl font-semibold text-base-content">Project Settings</h1>
                        </div>
                        <p className="text-muted ml-9">
                            Configure settings for <span className="font-medium text-base-content">{repo.name}</span>
                        </p>
                    </div>

                    <div className="mb-6 flex items-center justify-between gap-4 border border-primary/30 bg-primary/5 p-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium text-base-content">
                                <FlaskConical size={15} className="text-primary" /> Diff Lab
                            </div>
                            <p className="mt-1 text-xs text-muted">
                                Compare the current Pierre diff with whole-file, move-aware, low-noise, and Difftastic strategies.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate.go("CodeWorkspaceDiffLab", { workspaceId })}
                            className="btn shrink-0 border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-base-content hover:bg-primary/20"
                        >
                            Open lab
                        </button>
                    </div>

                    <div className="flex flex-col gap-6">
                        {/* Project Name */}
                        <div>
                            <label htmlFor="workspace-name" className="block text-sm font-medium text-base-content mb-2">
                                Project Name
                            </label>
                            <input
                                id="workspace-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="My Project"
                                className="w-full px-4 py-3 bg-input text-base-content border border-border focus:outline-none focus:border-primary transition-all placeholder:text-muted/50"
                            />
                            {name.trim().length === 0 && <p className="mt-1 text-xs text-error">Name is required</p>}
                        </div>

                        <fieldset className="border border-border bg-base-100">
                            <legend className="ml-3 px-1 text-sm font-medium text-base-content">Project identity</legend>
                            <div className="grid gap-5 p-4 sm:grid-cols-[1fr_auto]">
                                <div className="min-w-0">
                                    <div className="mb-2 text-sm font-medium text-base-content">Color</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            className={`btn flex h-8 items-center gap-2 border px-2 text-xs ${
                                                colorSelection === "auto"
                                                    ? "border-primary bg-primary/10 text-base-content"
                                                    : "border-border text-muted hover:bg-base-200 hover:text-base-content"
                                            }`}
                                            onClick={() => setColorSelection("auto")}
                                            aria-pressed={colorSelection === "auto"}
                                        >
                                            <span
                                                className="h-2.5 w-2.5 rounded-full"
                                                style={{ backgroundColor: automaticColors.get(repo.id) ?? automaticProjectColor(repo.id) }}
                                            />
                                            Automatic
                                        </button>
                                        {PROJECT_COLOR_OPTIONS.map((option) => {
                                            const selected = colorSelection === option.id
                                            return (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    className={`btn flex h-8 w-8 items-center justify-center border ${
                                                        selected
                                                            ? "border-base-content bg-base-200"
                                                            : "border-transparent hover:border-border hover:bg-base-200"
                                                    }`}
                                                    onClick={() => setColorSelection(option.id)}
                                                    title={option.label}
                                                    aria-label={option.label}
                                                    aria-pressed={selected}
                                                >
                                                    <span
                                                        className="flex h-4 w-4 items-center justify-center rounded-full"
                                                        style={{ backgroundColor: option.value }}
                                                        aria-hidden="true"
                                                    >
                                                        {selected ? <Check className="h-3 w-3 text-white drop-shadow-sm" strokeWidth={3} /> : null}
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>

                                    <div className="mt-5 flex flex-wrap items-end gap-3">
                                        <label className="text-sm font-medium text-base-content" htmlFor="workspace-emoji">
                                            Emoji
                                            <input
                                                id="workspace-emoji"
                                                type="text"
                                                value={projectEmoji}
                                                onChange={(event) => setProjectEmoji(event.target.value)}
                                                placeholder="✨"
                                                disabled={generatingIdentity}
                                                className="mt-2 block h-11 w-16 border border-border bg-input text-center text-2xl outline-none focus:border-primary"
                                            />
                                        </label>
                                        <label className="text-sm font-medium text-base-content" htmlFor="workspace-chars">
                                            Initials
                                            <input
                                                id="workspace-chars"
                                                type="text"
                                                value={projectChars}
                                                onChange={(event) => setProjectChars(normalizeProjectChars(event.target.value))}
                                                placeholder={defaultChars}
                                                disabled={generatingIdentity}
                                                className="mt-2 block h-11 w-20 border border-border bg-input text-center text-base font-semibold tracking-wide outline-none focus:border-primary"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="btn flex h-11 items-center gap-2 px-2 text-xs text-primary disabled:opacity-50"
                                            onClick={() => void handleGenerateIdentity()}
                                            disabled={generatingIdentity || !name.trim()}
                                        >
                                            {generatingIdentity ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                            {generatingIdentity ? "Choosing…" : "Suggest with AI"}
                                        </button>
                                    </div>
                                    <p className="mt-2 text-xs leading-5 text-muted">
                                        One emoji and two initials, saved with this project and reused throughout the app.
                                    </p>
                                    {identityError && (
                                        <p className="mt-2 text-xs text-error" role="alert">
                                            {identityError}
                                        </p>
                                    )}
                                    {!validIdentity && <p className="mt-2 text-xs text-error">Use two letters or digits and a single emoji.</p>}
                                </div>

                                <div className="flex items-start justify-center sm:pt-7">
                                    <span
                                        className="inline-flex h-12 w-12 items-center justify-center rounded-[7px] border"
                                        style={{
                                            backgroundColor: `color-mix(in srgb, ${previewColor} 11%, transparent)`,
                                            borderColor: `color-mix(in srgb, ${previewColor} 42%, transparent)`,
                                            color: previewColor,
                                        }}
                                        title="Project tile preview"
                                        aria-label="Project emoji preview"
                                    >
                                        <ProjectIcon
                                            projectId={repo.id}
                                            projectName={name}
                                            color={previewColor}
                                            projectChars={projectChars}
                                            projectEmoji={projectEmoji}
                                            variant="glyph"
                                            nativeTitle={false}
                                        />
                                    </span>
                                    <span className="ml-2 mt-3" aria-label="Project initials preview">
                                        <ProjectIcon
                                            projectId={repo.id}
                                            projectName={name}
                                            color={previewColor}
                                            projectChars={projectChars}
                                            projectEmoji={projectEmoji}
                                            nativeTitle={false}
                                        />
                                    </span>
                                </div>
                            </div>
                        </fieldset>

                        {/* Directory Path */}
                        <div>
                            <label htmlFor="workspace-path" className="block text-sm font-medium text-base-content mb-2">
                                Directory Path
                            </label>
                            <div className="flex gap-2">
                                <input
                                    id="workspace-path"
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder="~/Projects/my-app or /path/to/directory"
                                    className="flex-1 px-4 py-3 bg-input text-base-content border border-border focus:outline-none focus:border-primary transition-all placeholder:text-muted/50"
                                />
                                {hasCodeModules && canBrowseHostDirectories && (
                                    <button
                                        type="button"
                                        onClick={handleBrowse}
                                        className="btn flex cursor-pointer items-center gap-2 border border-border bg-base-200 px-4 py-3 text-base-content transition-all hover:bg-base-300"
                                    >
                                        <FolderOpen size="1em" />
                                        Browse
                                    </button>
                                )}
                            </div>
                            {/* Resolved path hint */}
                            {pathInfo && pathInfo.resolvedPath !== inputValue.trim() && (
                                <p className="mt-1 text-xs text-muted">Resolves to: {pathInfo.resolvedPath}</p>
                            )}
                            {/* Path validation feedback */}
                            {inputValue.trim() && !isValidating && pathInfo && !pathInfo.exists && (
                                <p className="mt-1 text-xs text-error">Directory does not exist</p>
                            )}
                            {inputValue.trim() && !isValidating && pathInfo && pathInfo.exists && !pathInfo.isDirectory && (
                                <p className="mt-1 text-xs text-error">Path is not a directory</p>
                            )}
                        </div>

                        {/* Git Warning Banner */}
                        {showGitWarning && (
                            <div className="p-4 bg-warning/10 border border-warning/30">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle size="1.25rem" className="text-warning flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <h3 className="text-sm font-semibold text-base-content mb-1">Not a Git Repository</h3>
                                        <p className="text-sm text-muted mb-3">
                                            This directory is not under git version control. The agent may make changes that cannot be recovered. We recommend
                                            initializing a git repository first.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleInitGit}
                                            disabled={isInitializingGit}
                                            className={`btn px-4 py-2 text-sm font-medium transition-all ${
                                                isInitializingGit
                                                    ? "bg-base-200/40 text-base-content/50 cursor-not-allowed"
                                                    : "bg-base-200 text-base-content hover:bg-base-300 cursor-pointer"
                                            }`}
                                        >
                                            {isInitializingGit ? (
                                                <>
                                                    <Loader2 size="1em" className="inline animate-spin mr-2" />
                                                    Initializing...
                                                </>
                                            ) : (
                                                <>
                                                    <GitBranch size="1em" className="inline mr-2" />
                                                    Initialize Git Repository
                                                </>
                                            )}
                                        </button>
                                        {initGitError && <p className="mt-2 text-sm text-error">{initGitError}</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Git repo success indicator */}
                        {hasValidDirectory && isGitRepo && !isValidating && (
                            <div className="p-3 bg-success/10 border border-success/30">
                                <div className="flex items-center gap-2">
                                    <GitBranch size="1em" className="text-success" />
                                    <span className="text-sm text-base-content">Git repository detected (default branch: {gitInfo.mainBranch})</span>
                                </div>
                            </div>
                        )}

                        {/* Validating indicator */}
                        {isValidating && (
                            <div className="flex items-center gap-2 text-muted">
                                <Loader2 size="1em" className="animate-spin" />
                                <span className="text-sm">Checking directory...</span>
                            </div>
                        )}

                        {/* Save Button */}
                        <div className="flex flex-col sm:flex-row gap-3 mt-2">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!canSave}
                                className={`btn flex flex-1 items-center justify-center gap-2 px-5 py-3 text-sm font-medium transition-all ${
                                    !canSave
                                        ? "bg-primary/40 text-primary-content/50 cursor-not-allowed"
                                        : "bg-primary text-primary-content hover:bg-primary/90 cursor-pointer"
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 size="1em" className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size="1em" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ScrollArea>
    )
})
